import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import {
  ListMasterDataPdfRequest,
  MasterDataPdfActive,
  MasterDataPdfAddressData,
  MasterDataPdfDefault,
  MasterDataPdfFooterData,
  MasterDataPdfHeaderData,
  MasterDataPdfInfoblockData,
  MasterDataPdfList,
} from "@/services/backend/company/types/master-data-pdf";

const api = backendApi.enhanceEndpoints({
  // The assignment has a tag of its own, so ticking one row does not
  // invalidate the list the table renders from.
  addTagTypes: ["MasterDataPdf", "ActiveMasterDataPdf"],
});

const masterDataPdfApi = api.injectEndpoints({
  endpoints: (build) => ({
    createMasterDataPdf: build.mutation<
      MasterDataPdfDefault,
      CreateMasterDataPdfRequest
    >({
      query: (request) => ({
        url: `/companies/master-data/pdf`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "MasterDataPdf", id: "LIST" }],
    }),
    patchMasterDataPdf: build.mutation<
      MasterDataPdfDefault,
      PatchMasterDataPdfRequest
    >({
      query: (request) => ({
        url: `/companies/master-data/pdf`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "MasterDataPdf", id: request.id },
        { type: "MasterDataPdf", id: "LIST" },
      ],
    }),
    showMasterDataPdf: build.query<
      MasterDataPdfDefault,
      ShowMasterDataPdfRequest
    >({
      query: (request) => ({
        url: `/companies/master-data/pdf`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "MasterDataPdf", id: result.id }] : [],
    }),
    listMasterDataPdf: build.query<MasterDataPdfList, ListMasterDataPdfRequest>(
      {
        query: (request) => ({
          url: `/companies/master-data/pdf/list`,
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) => providesList(result?.items, "MasterDataPdf"),
      },
    ),
    putActiveMasterDataPdf: build.mutation<
      MasterDataPdfActive,
      PutActiveMasterDataPdfRequest
    >({
      query: (request) => ({
        url: `/companies/master-data/pdf/active`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "ActiveMasterDataPdf", id: request.companyId },
      ],
    }),
    showActiveMasterDataPdf: build.query<
      MasterDataPdfActive,
      ShowActiveMasterDataPdfRequest
    >({
      query: (request) => ({
        url: `/companies/master-data/pdf/active`,
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _error, request) => [
        { type: "ActiveMasterDataPdf", id: request.companyId },
      ],
    }),
    deleteMasterDataPdf: build.mutation<null, DeleteMasterDataPdfRequest>({
      query: (request) => ({
        url: `/companies/master-data/pdf`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: [{ type: "MasterDataPdf", id: "LIST" }],
    }),
  }),
});

export const updateMasterDataPdfCache = updateCache(
  masterDataPdfApi,
  "showMasterDataPdf",
);

export const {
  useCreateMasterDataPdfMutation,
  usePutActiveMasterDataPdfMutation,
  useShowActiveMasterDataPdfQuery,
  usePatchMasterDataPdfMutation,
  useShowMasterDataPdfQuery,
  useListMasterDataPdfQuery,
  useDeleteMasterDataPdfMutation,
} = masterDataPdfApi;

export interface CreateMasterDataPdfRequest {
  id: string;
  companyId: string;
  label: string;
}

/**
 * PatchMasterDataPdfRequest updates the master data. All fields
 * except `id` are optional: an omitted key leaves the corresponding
 * value unchanged (the backend `patch.Field` is "unset" when the
 * JSON key is absent).
 */
export interface PatchMasterDataPdfRequest {
  id: string;
  label?: string;
  addressData?: MasterDataPdfAddressData;
  headerData?: MasterDataPdfHeaderData;
  footerData?: MasterDataPdfFooterData;
  infoblockData?: MasterDataPdfInfoblockData;
  linkToAgb?: string;
}

export interface ShowMasterDataPdfRequest {
  id: string;
}

/**
 * PutActiveMasterDataPdfRequest assigns the master data a company's
 * documents are rendered with. A null defaultId clears the
 * assignment.
 */
export interface PutActiveMasterDataPdfRequest {
  companyId: string;
  defaultId: string | null;
}

export interface ShowActiveMasterDataPdfRequest {
  companyId: string;
}

export interface DeleteMasterDataPdfRequest {
  id: string;
}
